import { NotificationType, usePersona, useTranslation } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { IconVariants } from "@next-platform/client-common-spicy-ui";
import { ConversationSortOrder, DeleteConversationResponse, UpdateConversationLabelResponse } from "@next-platform/packages-ts-types";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import { useCallback, useEffect, useRef, useState } from "react";

import useApi from "@/components/useApi";
import { IChatsConversation } from "@/types";

import useToast from "./useToast";

const logger = appLogger.withTag("useSharedChats");

type RefetchSortedConversations = (sort: ConversationSortOrder) => Promise<unknown>;

export function useSharedChats() {
  const { t } = useTranslation();
  const { jsonApiService, callApi: callRemoveConversationApi, isLoading: isRemoveConversationLoading } = useApi<DeleteConversationResponse>();
  const { callApi: callRemoveSelectedConversationsApi, isLoading: isRemoveSelectedConversationsLoading } = useApi<DeleteConversationResponse>();
  const { callApi: callChangeTitleApi, isLoading: isChangeTitleLoading } = useApi<{ result: UpdateConversationLabelResponse }>();
  const { displayToast } = useToast();

  const deleteConversationResource = async (resource: string, id: string | null, data?: Record<string, unknown>) => {
    const response = await jsonApiService.deleteResource(resource, id, data);
    return response.data as DeleteConversationResponse;
  };

  const [selectedConversation, setSelectedConversation] = useState<IChatsConversation | null>(null);
  const [conversations, setConversations] = useState<IChatsConversation[]>([]);

  const [isChangeTitleModalVisible, setIsChangeTitleModalVisible] = useState(false);
  const [isRemoveConversationModalVisible, setIsRemoveConversationModalVisible] = useState(false);
  const [stateIsLoadingMoreConversations, setStateIsLoadingMoreConversations] = useState(false);
  const [stateIsSortRefetching, setStateIsSortRefetching] = useState(false);
  const [stateSortOrder, setStateSortOrder] = useState<ConversationSortOrder>(ConversationSortOrder.LATEST);
  const refSortOrder = useRef(stateSortOrder);
  refSortOrder.current = stateSortOrder;
  const refAppliedSort = useRef(stateSortOrder);
  const refIsPageOneInFlight = useRef(false);
  const refSortRequestId = useRef(0);

  const { getUserPersonaById } = usePersona();
  const [searchQuery, setSearchQuery] = useState("");

  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedConversationIds, setSelectedConversationIds] = useState<Set<string>>(new Set());
  const [isSelectAllActive, setIsSelectAllActive] = useState(false);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    if (isMultiSelectMode) {
      // Clear selections when exiting multi-select mode
      setSelectedConversationIds(new Set());
      setIsSelectAllActive(false);
    }
  };

  const getFilteredConversations = useCallback(
    (conversationsList: IChatsConversation[]) => {
      if (!searchQuery.trim()) {
        return conversationsList;
      }
      const query = searchQuery.toLowerCase();
      return conversationsList.filter((conversationItem: IChatsConversation) => {
        const chatTitle = conversationItem?.label || conversationItem?.character?.name || "";
        const personaName = getUserPersonaById(conversationItem?.user_persona_id)?.name || "";
        const searchText = `${chatTitle} ${personaName}`.toLowerCase();
        return searchText.includes(query);
      });
    },
    [searchQuery],
  );

  const filteredConversations = getFilteredConversations(conversations);

  const toggleConversationSelection = (conversationId: string) => {
    const updatedConversationIds = new Set(selectedConversationIds);
    if (updatedConversationIds.has(conversationId)) {
      updatedConversationIds.delete(conversationId);
    } else {
      updatedConversationIds.add(conversationId);
    }
    setSelectedConversationIds(updatedConversationIds);
    setIsSelectAllActive(updatedConversationIds.size === conversations.length);
  };

  const selectAllConversations = (selectAll: boolean) => {
    const updatedIds = new Set(selectedConversationIds);
    filteredConversations.forEach((c: IChatsConversation) => {
      if (selectAll) {
        updatedIds.add(c.id);
      } else {
        updatedIds.delete(c.id);
      }
    });
    setSelectedConversationIds(updatedIds);
    setIsSelectAllActive(updatedIds.size === conversations.length);
  };

  const cancelMultiSelect = () => {
    setIsMultiSelectMode(false);
    setSelectedConversationIds(new Set());
    setIsSelectAllActive(false);
  };

  const openRemoveModalForMultiSelect = () => {
    setIsRemoveConversationModalVisible(true);
  };

  // Auto-select newly loaded conversations when "select all" is active
  useEffect(() => {
    if (isSelectAllActive && conversations.length > 0 && selectedConversationIds.size !== conversations.length) {
      const allIds = new Set(conversations.map((c: IChatsConversation) => c.id));
      setSelectedConversationIds(allIds);
    }
  }, [conversations, isSelectAllActive, selectedConversationIds.size]);

  const getCommonDropdownListItems = useCallback(
    (m: IChatsConversation) => {
      return {
        changeTitle: {
          label: t("common:changeTitle"),
          onPress: () => {
            setSelectedConversation({ ...m, label: m?.label || m?.character?.name || "" });
            setIsChangeTitleModalVisible(true);
          },
          icon: IconVariants.EDIT,
        },
        removeConversation: {
          label: t("common:removeConversation"),
          onPress: () => {
            setSelectedConversation(m);
            setIsRemoveConversationModalVisible(true);
          },
          icon: IconVariants.TRASH,
          className: "*:text-red-9",
        },
      };
    },
    [setSelectedConversation, setIsChangeTitleModalVisible, setIsRemoveConversationModalVisible, t],
  );

  const onUserRemoveConversation = async () => {
    try {
      // Multiple conversation deletion
      if (isMultiSelectMode && selectedConversationIds.size > 0) {
        const idsToRemove = Array.from(selectedConversationIds);
        const payload = { conversation_ids: idsToRemove };

        await callRemoveSelectedConversationsApi(deleteConversationResource, "conversations", null, payload);

        const newConversations = conversations.filter((c: IChatsConversation) => !selectedConversationIds.has(c?.id));
        setConversations(newConversations);
        displayToast({
          message: selectedConversationIds.size > 1 ? t("conversation:toast.success.removed_other") : t("conversation:toast.success.removed_one"),
        });
        setSelectedConversationIds(new Set());
        setIsMultiSelectMode(false);
        setIsSelectAllActive(false);
      } else {
        // Single conversation deletion
        await callRemoveConversationApi(deleteConversationResource, "conversations", selectedConversation?.id as string, undefined);
        const newConversations = conversations.filter((c: IChatsConversation) => c?.id !== selectedConversation?.id);
        setConversations(newConversations);
        displayToast({ message: t("conversation:toast.success.removed_one") });
      }
    } catch (error) {
      logger.error(
        "error while removing conversation",
        { conversationId: selectedConversation?.id, selectedIds: Array.from(selectedConversationIds) },
        error,
      );
      displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
    } finally {
      setIsRemoveConversationModalVisible(false);
      setSelectedConversation(null);
    }
  };

  const onUpdateConversationTitle = async (newTitle: string) => {
    const conversationId = selectedConversation?.id;
    const payload = { label: newTitle };
    try {
      const response = await callChangeTitleApi(jsonApiService.updateResource, `conversations/${conversationId}/label`, "", payload);
      // Update the conversation in the list
      const updatedConversations = conversations.map((c: IChatsConversation) =>
        c.id === conversationId ? { ...c, label: response?.result?.label as string } : c,
      );
      setConversations(updatedConversations);
      displayToast({ message: t("conversation:toast.success.titleChanged"), type: NotificationType.success });
    } catch (error: unknown) {
      logger.error("error while updating conversation title", { conversationId }, error);
      displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
    } finally {
      setIsChangeTitleModalVisible(false);
      setSelectedConversation(null);
    }
  };

  const convertTime = (time?: string | number | Date) => {
    if (!time) return "";
    try {
      return formatDistanceToNow(new Date(time as string), { addSuffix: true });
    } catch (err) {
      logger.error("[Chats Page] error while converting time", { time }, err);
      return "";
    }
  };

  const getActiveSortOrder = useCallback(() => refSortOrder.current, []);

  const setPageOneInFlight = useCallback((inFlight: boolean) => {
    refIsPageOneInFlight.current = inFlight;
  }, []);

  const isPageOneBlockingLoadMore = useCallback(() => {
    return refIsPageOneInFlight.current || refSortOrder.current !== refAppliedSort.current;
  }, []);

  const markSortApplied = useCallback((sort: ConversationSortOrder) => {
    refAppliedSort.current = sort;
    // Keep dropdown in sync when a page-one fetch succeeds (recovers if a stale failure reverted early).
    refSortOrder.current = sort;
    setStateSortOrder(sort);
  }, []);

  const handleSortChange = useCallback(
    (sort: ConversationSortOrder, refetchSortedConversations: RefetchSortedConversations) => {
      if (sort === refSortOrder.current) {
        return;
      }

      const sortRequestId = ++refSortRequestId.current;
      refSortOrder.current = sort;
      setStateSortOrder(sort);
      setStateIsSortRefetching(true);

      void refetchSortedConversations(sort)
        .catch(() => {
          // A newer sort click already owns the dropdown; do not roll that back.
          if (refSortOrder.current !== sort) {
            return;
          }
          // A concurrent fetch already applied this sort successfully.
          if (refAppliedSort.current === sort) {
            return;
          }
          refSortOrder.current = refAppliedSort.current;
          setStateSortOrder(refAppliedSort.current);
          displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
        })
        .finally(() => {
          if (sortRequestId === refSortRequestId.current) {
            setStateIsSortRefetching(false);
          }
        });
    },
    [displayToast, t],
  );

  const runLoadMore = useCallback(
    async (loadMoreFn: () => Promise<unknown>) => {
      if (isPageOneBlockingLoadMore() || stateIsLoadingMoreConversations) {
        return;
      }

      setStateIsLoadingMoreConversations(true);
      try {
        await loadMoreFn();
      } catch {
        displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
      } finally {
        setStateIsLoadingMoreConversations(false);
      }
    },
    [displayToast, isPageOneBlockingLoadMore, stateIsLoadingMoreConversations, t],
  );

  return {
    filteredConversations,
    conversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    isChangeTitleModalVisible,
    setIsChangeTitleModalVisible,
    isRemoveConversationModalVisible,
    setIsRemoveConversationModalVisible,
    onUserRemoveConversation,
    onUpdateConversationTitle,
    convertTime,
    isRemoveConversationLoading: isRemoveConversationLoading || isRemoveSelectedConversationsLoading,
    isChangeTitleLoading,
    getCommonDropdownListItems,
    handleSearch,
    handleClearSearch,
    searchQuery,
    sortOrder: stateSortOrder,
    isSortRefetching: stateIsSortRefetching,
    setIsSortRefetching: setStateIsSortRefetching,
    isLoadingMoreConversations: stateIsLoadingMoreConversations,
    getActiveSortOrder,
    markSortApplied,
    setPageOneInFlight,
    isPageOneBlockingLoadMore,
    handleSortChange,
    runLoadMore,
    // Multi-select
    multiSelectProps: {
      isMultiSelectMode,
      selectedConversationIds,
      toggleMultiSelectMode,
      toggleConversationSelection,
      selectAllConversations,
      cancelMultiSelect,
      openRemoveModalForMultiSelect,
    },
  };
}

export default useSharedChats;
